/**
 * VerifiedSiteData — Section A of the restructured panel.
 *
 * Renders ONLY data we measured from authoritative sources, using
 * the DataRow primitive (label · value · source). Calculations,
 * estimates, and inferred values do NOT belong here — they go in
 * EstimatesSection.
 *
 * Subsections:
 *   1. Parcel              (Kartverket Matrikkel)
 *   2. Grid Infrastructure (NVE Nettanlegg)
 *   3. Terrain             (Kartverket DTM / SRTM)
 *   4. Climate             (YR.no / FMI)
 *   5. Network             (PeeringDB)
 *   6. Environmental       (NVE / Miljødirektoratet / Riksantikvaren)
 *   7. Water               (NVE)
 *
 * Each row is skipped automatically when the value is null/empty
 * (see DataRow). So this section adapts to data availability without
 * showing "—" filler.
 *
 * Audit 2026-05-27 (restructure): introduced so verified facts and
 * speculative estimates don't share visual weight.
 */

import React, { useState, useEffect, useRef } from 'react';
import DataRow from './DataRow';
import { ProvenanceSite } from './ProvenancePopover';
import { seEiendomUrl, wattappStationUrl, norgeskartUrl, recordLink } from '../utils/sourceLinks';
import { ZONING_STATUS_LABEL, capacityAge } from '../config/displaySchema';
import { planCodeName } from '../utils/planCodes';
import { nearestHomeText } from '../utils/format';

// Collapsible, search-filterable group. When `query` is non-empty the
// group inspects its DataRow children, hides ones that don't match
// (label / value / source), and auto-expands itself if any match. The
// whole group hides when query is set and nothing matches.
// Collapsed state is per-mount (no localStorage) — defaults to open.
const Group = ({ title, defaultOpen = true, query, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const q = (query || '').trim().toLowerCase();
  const childList = React.Children.toArray(children).filter(Boolean);
  let visibleChildren = childList;
  if (q) {
    const titleMatches = (title || '').toLowerCase().includes(q);
    visibleChildren = childList.filter(child => {
      if (!React.isValidElement(child)) return true;
      if (titleMatches) return true;
      const p = child.props || {};
      const valueStr = typeof p.value === 'string' ? p.value
                     : typeof p.value === 'number' ? String(p.value)
                     : (p.searchText || '');
      const haystack = [p.label, p.source, p.title, valueStr]
        .filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
    if (visibleChildren.length === 0) return null;
  }
  const isOpen = q ? true : open;
  const matchCount = q ? visibleChildren.length : null;
  return (
    <div className="data-group">
      <button
        type="button"
        onClick={() => !q && setOpen(o => !o)}
        className="section-header"
        style={{
          width: '100%', textAlign: 'left', background: 'none',
          border: 'none', cursor: q ? 'default' : 'pointer',
          padding: '6px 0', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
          color: 'inherit', font: 'inherit',
        }}>
        <span>
          <span style={{ display: 'inline-block', width: 12, color: '#64748b' }}>
            {isOpen ? '▾' : '▸'}
          </span>
          {title}
        </span>
        {matchCount != null && (
          <span style={{ fontSize: 10, color: '#64748b', fontWeight: 400 }}>
            {matchCount} match{matchCount === 1 ? '' : 'es'}
          </span>
        )}
      </button>
      {isOpen && <div>{visibleChildren}</div>}
    </div>
  );
};

// Translate Norwegian water-type tokens into English labels — the
// PostGIS rivers/lakes tables store "elv" / "innsjø" verbatim.
const WATER_TYPE_LABELS = {
  elv:        'River',
  innsjø:     'Lake',
  innsjo:     'Lake',
  sjø:        'Sea',
  sjo:        'Sea',
  kyst:       'Sea (coastline)',
  freshwater: 'Freshwater',
  saltwater:  'Sea',
  sea:        'Sea',
};

// Zone names come from the official plan-code lists (utils/planCodes),
// shared with the backend. The in-component table this replaced misread
// most codes (1300 Næringsbebyggelse as green structure, 1500 as LNF).
function resolveZone(code, name) {
  if (name && name.trim()) return name;
  return planCodeName(code);
}

export default function VerifiedSiteData({
  site,
  buildableDetail = null,
  buildableDetailLoading = false,
  buildableDetailError = null,
  onComputePrecise = null,
  jumpTargetField = null,
  onLocate = null,
  country = 'NO',
}) {
  if (!site) return null;
  // Links from a value to its record (utils/sourceLinks); null when the site
  // lacks the key, and the source then stays plain text.
  const parcelHref = seEiendomUrl(site);
  const terrainHref = country === 'NO' ? norgeskartUrl(site.lat, site.lon) : null;
  const terrainSource = country === 'NO' ? 'Kartverket terrain model' : 'SRTM 30m';
  // locate(kind, name, label) → undefined when the parent didn't wire the
  // map, so DataRow stays non-clickable. Kinds match /api/v1/sites/locate.
  const locate = (kind, name, label) =>
    (typeof onLocate === 'function' && name) ? () => onLocate(kind, name, label || name) : undefined;

  // Jump-to-field: when BottomLine's clickable counters fire and the
  // parent flips to the Data tab, scroll the matching row into view
  // and add a brief highlight class. rAF lets the tab content lay out
  // first so scrollIntoView lands on the right element.
  const rootRef = useRef(null);
  useEffect(() => {
    if (!jumpTargetField) return;
    const r = requestAnimationFrame(() => {
      const root = rootRef.current;
      if (!root) return;
      const el = root.querySelector(`[data-field="${CSS.escape(jumpTargetField)}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('jump-flash');
      setTimeout(() => el.classList.remove('jump-flash'), 1800);
    });
    return () => cancelAnimationFrame(r);
  }, [jumpTargetField]);

  const lat = site.lat ?? site.latitude;
  const lon = site.lon ?? site.longitude;
  const coords = (lat != null && lon != null)
    ? `${Number(lat).toFixed(4)}°N · ${Number(lon).toFixed(4)}°E`
    : null;

  const muniName = site.municipality_name || site.zoning_info?.municipality;
  const muniNo   = site.municipality_number || site.zoning_info?.municipality_number;

  // Slope max display — flag as concern at >35% (audit hard limit).
  const slopeMax = site.slope_max_pct;
  const slopeMaxStatus = slopeMax == null ? null
                        : slopeMax > 35 ? 'flagged' : 'verified';

  // Search across all DataRow labels / values / sources / titles.
  // When non-empty, Groups auto-expand and hide non-matching rows;
  // empty groups disappear entirely. Cleared via the × button.
  const [query, setQuery] = useState('');

  return (
    <ProvenanceSite.Provider value={site}>
    <div ref={rootRef} style={{ padding: '14px 18px 0' }}>
      <div className="section-header" style={{ fontSize: 12, marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #1f2937' }}>
        Verified Site Data
      </div>
      <div style={{
        position: 'relative', marginBottom: 12,
      }}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search site data — substation, fiber, slope, zone…"
          style={{
            width: '100%', padding: '7px 28px 7px 10px', fontSize: 12,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 4, color: '#e2e8f0', outline: 'none',
          }}
        />
        {query && (
          <button onClick={() => setQuery('')} title="Clear"
            style={{
              position: 'absolute', right: 6, top: '50%',
              transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#64748b', fontSize: 14, padding: '0 4px',
            }}>×</button>
        )}
      </div>

      <Group title="Parcel" query={query}>
        <DataRow label="Parcel ID"     value={site.teig_id}        source="Kartverket Matrikkel" sourceHref={parcelHref} />
        <DataRow label="Gnr/Bnr"       value={site.gnr_bnr || (site.gnr && site.bnr ? `${site.gnr}/${site.bnr}` : null)} source="Kartverket Matrikkel" sourceHref={parcelHref} />
        <DataRow label="Coordinates"   value={coords}              source="Kartverket Matrikkel" />
        <DataRow label="Municipality"  value={muniName ? (muniNo ? `${muniName} (${muniNo})` : muniName) : null} source="Kartverket Matrikkel" mono={false} />
        <DataRow label="Total area"    value={site.total_area_ha != null ? `${site.total_area_ha.toFixed(1)} ha` : null} source="Kartverket Matrikkel" sourceHref={parcelHref} />
        {(() => {
          // The dates the site's facts rest on (review gap 5, 2026-10-18); the
          // same parts as the Projects card's "Data as of" row.
          const day = (v) => (v ? String(v).slice(0, 10) : null);
          const gp = site.harbour_screen?.grid_point;
          const parts = [
            day(site.parcel_snapshot_date) ? `parcels ${day(site.parcel_snapshot_date)}` : null,
            day(site.plan_readings?.fetched_at) ? `plans read ${day(site.plan_readings.fetched_at)}` : null,
            day(gp?.updated_at) ? `capacity published ${day(gp.updated_at)}` : null,
            day(site.statnett_above?.snapshot_date) ? `Statnett ${day(site.statnett_above.snapshot_date)}` : null,
            day(site.refreshed_at || site.enriched_at) ? `site read ${day(site.refreshed_at || site.enriched_at)}` : null,
          ].filter(Boolean);
          return parts.length ? <DataRow label="Data as of" value={parts.join(' · ')} source="SEIDR, from the dates each source carries" mono={false} field="data_dates" country={country} /> : null;
        })()}
        {site.capacity_effective_buildable_ha != null && site.total_area_ha != null && (() => {
          const est       = site.capacity_effective_buildable_ha;
          const tot       = site.total_area_ha;
          const estPct    = tot > 0 ? Math.round((est / tot) * 100) : null;
          const precise   = buildableDetail?.buildable_area_ha;
          const hasPrecise = precise != null;
          const precisePct = hasPrecise && tot > 0 ? Math.round((precise / tot) * 100) : null;
          const delta      = hasPrecise ? precise - est : null;
          const deltaPct   = hasPrecise && est > 0 ? Math.round((delta / est) * 100) : null;
          // Narration for the delta — short hint about what likely
          // drove the precise number above or below the estimate.
          let deltaHint = null;
          if (hasPrecise && Math.abs(delta) >= 0.5) {
            if (delta > 0) {
              const slopeDist = buildableDetail?.slope_distribution || {};
              const flatPct = Math.round(((slopeDist.flat || 0) + (slopeDist.gentle || 0)) * 100);
              deltaHint = flatPct >= 60
                ? ` — flat valley shelf detected (${flatPct}% of cells ≤15% slope)`
                : ' — flat pockets larger than centroid suggested';
            } else {
              deltaHint = ' — slope distribution worse than centroid suggested';
            }
          }
          return (
            <>
              <DataRow
                label={hasPrecise ? "Estimated (screening)" : "Net buildable (today-fit)"}
                value={`${est.toFixed(1)} ha${estPct != null ? ` (${estPct}% of total)` : ''}`}
                source="site_capacity · slope + setbacks + roads + utilities"
              />
              {hasPrecise && (
                <DataRow
                  label="Precise (slope + edge setback) ✓"
                  value={(
                    <span>
                      {precise.toFixed(1)} ha{precisePct != null ? ` (${precisePct}% of total)` : ''}
                      {delta != null && (
                        <span style={{ color: delta >= 0 ? '#22c55e' : '#f59e0b', marginLeft: 8, fontSize: 11 }}>
                          {delta >= 0 ? '+' : ''}{delta.toFixed(1)} ha
                          {deltaPct != null && Math.abs(deltaPct) >= 5 && ` (${deltaPct > 0 ? '+' : ''}${deltaPct}%)`}
                          {deltaHint}
                        </span>
                      )}
                    </span>
                  )}
                  source={(() => {
                    const gn = buildableDetail.grid_n || 10;
                    const sb = buildableDetail.setback_buffer_m;
                    const slopeMax = buildableDetail.max_slope_threshold_pct;
                    const waterHa = buildableDetail.water_subtracted_ha;
                    const cs = buildableDetail.cache_status === 'cached' ? 'cached' : 'fresh';
                    const parts = [`${gn}×${gn} DEM grid`];
                    if (slopeMax != null) parts.push(`≤${Math.round(slopeMax)}% slope`);
                    if (sb) parts.push(`${sb} m setback`);
                    if (waterHa && waterHa > 0.01) parts.push(`-${waterHa.toFixed(1)} ha water`);
                    parts.push(cs);
                    return parts.join(' · ');
                  })()}
                  mono={false}
                />
              )}
              {!hasPrecise && onComputePrecise && (
                <div style={{ padding: '4px 0 8px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button
                    onClick={onComputePrecise}
                    disabled={buildableDetailLoading}
                    style={{
                      fontSize: 11, padding: '4px 10px', borderRadius: 4,
                      background: buildableDetailLoading ? '#1f2937' : '#1e3a5f',
                      color: '#dbeafe',
                      border: '1px solid #2563eb',
                      cursor: buildableDetailLoading ? 'wait' : 'pointer',
                    }}
                  >
                    {buildableDetailLoading ? 'Computing… (~5–15 s)' : 'Compute precise buildable area'}
                  </button>
                  {buildableDetailError && (
                    <span style={{ fontSize: 11, color: '#f87171' }}>
                      {buildableDetailError}
                    </span>
                  )}
                </div>
              )}
            </>
          );
        })()}
        {site.constituent_count > 1 && (
          <DataRow label="Parcel count" value={`${site.constituent_count} parcels`} source="Assembly pipeline" />
        )}
      </Group>

      {site.harbour_screen && (() => {
        // Harbour + published grid capacity screen (2026-10-07).
        const s = site.harbour_screen;
        const gp = s.grid_point || {};
        const hb = s.harbour || {};
        const area = s.statnett_area;
        const stationHref = wattappStationUrl(gp);
        const at = (kind, name, label, p) => (typeof onLocate === 'function' && p && Number.isFinite(p.lat) && Number.isFinite(p.lon))
          ? () => onLocate(kind, name, label, { lat: p.lat, lon: p.lon }) : undefined;
        return (
          <Group title="Harbour + capacity screen" query={query}>
            <DataRow label="Meets every rule" value={`${s.qualifying_ha} ha of the ${s.parcel_area_ha} ha parcel`} source="SEIDR screen" mono={false} />
            <DataRow label="Grid point" value={`${gp.name}${gp.owner ? ` (${gp.owner})` : ''}${gp.is_area ? ' · area figure' : ''}`}
                     source="Elbits WattApp" sourceHref={stationHref} mono={false} onLocate={at('substation', gp.name, `${gp.name} · ${gp.available_mw} MW free`, gp)} />
            <DataRow label="Distance to grid point" value={gp.distance_km != null ? `${gp.distance_km} km (straight line)` : null} source="Spatial calc" mono={false} />
            <DataRow label="Available for consumption" value={gp.available_mw != null ? `${gp.available_mw} MW` : null}
                     source={gp.updated_at ? `WattApp · updated ${gp.updated_at}` : `WattApp · as of ${gp.data_date || 'unknown'}`} sourceHref={stationHref}
                     status={capacityAge(gp).stale ? 'flagged' : undefined} />
            {site.capacity_covers_plant != null && gp.available_mw != null && (() => {
              // Does the point cover the requested plant on its own? (review
              // gaps 2 and 3, 2026-10-18; the Projects card says the same.)
              const mw = site.plant_mw != null ? `${Math.round(site.plant_mw)} MW ` : '';
              const margin = site.capacity_margin_mw;
              const value = site.capacity_covers_plant
                ? `Yes — ${Math.round(gp.available_mw)} MW free for the ${mw}plant${margin != null ? ` (+${Math.round(margin)} MW)` : ''}`
                : `No — ${Math.round(gp.available_mw)} MW free, ${mw}needed${margin != null ? ` (${Math.round(margin)} MW)` : ''}`;
              return <DataRow label="Covers the plant" value={value} source={capacityAge(gp).text} sourceHref={stationHref}
                              status={site.capacity_covers_plant ? 'verified' : 'flagged'} mono={false} field="capacity_covers_plant" country={country} />;
            })()}
            <DataRow label="Reserved by others" value={gp.reserved_mw != null ? `${gp.reserved_mw} MW` : null} source="WattApp" sourceHref={stationHref} />
            <DataRow label="Queue (requested)" value={gp.queue_reported === false ? `${gp.queue_mw ?? 0} MW · company doesn't report a queue` : (gp.queue_mw != null ? `${gp.queue_mw} MW` : null)}
                     source="WattApp" sourceHref={stationHref} status={gp.queue_reported === false ? 'flagged' : undefined} mono={false} />
            {area && (
              <DataRow label="Statnett area queue" value={`${area.name}: ${area.requested_mw} MW requested`} source={`Statnett via WattApp · ${area.date || ''}`} mono={false} />
            )}
            <DataRow label="Harbour" value={`${hb.name}${hb.port && hb.port !== hb.name ? ` · ${hb.port}` : ''}`} source="Kystverket" mono={false}
                     onLocate={at('port', hb.name, hb.name, hb)} />
            <DataRow label="Distance to harbour" value={hb.distance_km != null ? `${hb.distance_km} km` : null} source="Spatial calc" mono={false} />
            <DataRow label="Port calls" value={hb.calls != null ? `${hb.calls} in ${hb.calls_year}${hb.gas_tanker_calls ? ` · ${hb.gas_tanker_calls} gas/tanker` : ''}` : null} source="SafeSeaNet" mono={false} />
            <DataRow label="Nearest home" value={nearestHomeText(s.nearest_home_m, s.limits?.min_neighbour_m).text} source="Kartverket Matrikkelen buildings" mono={false} />
            {s.terrain && (
              <DataRow label="Terrain" value={s.terrain} source="Kartverket terrain classes" mono={false} />
            )}
          </Group>
        );
      })()}

      {(() => {
        // Single source of truth — same fix as the result card. When
        // the capacity-first chain ran, the upstream-validated
        // substation (_capacityContext) is the analyst-relevant one
        // and the card displays it. The Analysis Panel must agree.
        const cc = site._capacityContext;
        const rawName = cc?.name || site.nearest_grid_name || site.nearest_grid?.name;
        const subKv   = cc?.voltage_kv ?? site.nearest_grid_voltage_kv ?? site.voltage_level_kv;
        const subKm   = cc?.distance_km ?? site.nearest_grid_distance_km;
        const subOp   = cc?.operator || site.nearest_grid_operator;
        const subSrc  = cc ? "Upstream capacity (seidr.makers.bot /assess-station)" : "NVE Nettanlegg";
        // When name is still useless (upstream returned "Unknown" AND
        // the local-name KNN found no non-generic match within 2 km),
        // build a fallback display so the analyst can at least identify
        // WHICH substation we anchored distance criteria on. Pulls
        // operator + voltage + coords + station_id.
        const _looksUnknown = !rawName || /^(unknown|null|none|n\/a)$/i.test(String(rawName).trim());
        let subName = rawName;
        if (_looksUnknown) {
          const parts = [];
          if (subOp) parts.push(subOp);
          if (subKv) parts.push(`${subKv} kV`);
          // station_id from upstream — short suffix to disambiguate two
          // unnamed FAGNE AS 66 kV substations near the same parcel.
          if (cc?.station_id) parts.push(`#${String(cc.station_id).slice(-6)}`);
          // Coords as a last visual hint when neither id nor name resolves.
          const lat = cc?.lat ?? site.nearest_grid?.lat ?? site.lat;
          const lng = cc?.lng ?? cc?.lon ?? site.nearest_grid?.lon ?? site.lon;
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            parts.push(`@ ${Number(lat).toFixed(3)}, ${Number(lng).toFixed(3)}`);
          }
          subName = parts.length > 0 ? `Unnamed — ${parts.join(' · ')}` : 'Unnamed (no metadata)';
        }
        // Step-up/step-down substations (one voltage tier away)
        const stepUp = site.step_up_substation;
        const stepDown = site.step_down_substation;
        return (
          <Group title="Grid Infrastructure" query={query}>
            <DataRow label="Connection substation" value={subName} source={subSrc} mono={false}
                     onLocate={locate('substation', subName, subKv != null ? `${subName} · ${subKv} kV` : subName)} field="nearest_grid_name" country={country} />
            <DataRow label="Voltage"            value={subKv != null ? `${subKv} kV` : null} source={subSrc} field="nearest_grid_voltage_kv" country={country} />
            <DataRow label="Distance"           value={subKm != null ? `${(typeof subKm === 'number' ? subKm : parseFloat(subKm)).toFixed(2)} km` : null} source="Spatial calc" field="nearest_grid_distance_km" country={country} />
            <DataRow label="Operator"           value={subOp} source={subSrc} mono={false} field="nearest_grid_operator" country={country} />
            <DataRow label="Price zone"         value={site.price_zone || null} source="Statnett price areas, by municipality" field="price_zone" country={country} />
            <DataRow label="Dual feed"          value={site.dual_feed_available === true ? 'Available' : site.dual_feed_available === false ? 'Single feed' : null} source="NVE Nettanlegg" mono={false} field="dual_feed_available" country={country} />
            {site.nearest_any_grid_name && (
              <DataRow label="Nearest substation (any kV)"
                       value={`${site.nearest_any_grid_name}${site.nearest_any_grid_voltage_kv != null ? ` · ${site.nearest_any_grid_voltage_kv} kV` : ''}${site.nearest_any_grid_distance_km != null ? ` · ${Number(site.nearest_any_grid_distance_km).toFixed(2)} km` : ''}`}
                       source="NVE Nettanlegg" mono={false}
                       onLocate={locate('substation', site.nearest_any_grid_name, `${site.nearest_any_grid_name} (nearest, any kV)`)} />
            )}
            {/* Step-up option: higher voltage tier (for expansion) */}
            {stepUp && (
              <DataRow 
                label="Step-up option" 
                onLocate={locate('substation', stepUp?.name || stepUp?.substation || (typeof stepUp === 'string' ? stepUp : null), 'Step-up substation')}
                value={`${stepUp.name || 'Unnamed'} · ${stepUp.voltage_kv} kV · ${stepUp.distance_km} km`} 
                source="NVE (one tier higher)" 
                mono={false} 
              />
            )}
            {/* Step-down option: lower voltage tier (cheaper, needs transformer) */}
            {stepDown && (
              <DataRow 
                label="Step-down option" 
                onLocate={locate('substation', stepDown?.name || stepDown?.substation || (typeof stepDown === 'string' ? stepDown : null), 'Step-down substation')}
                value={`${stepDown.name || 'Unnamed'} · ${stepDown.voltage_kv} kV · ${stepDown.distance_km} km`} 
                source="NVE (one tier lower)" 
                mono={false} 
              />
            )}
          </Group>
        );
      })()}

      <Group title="Terrain" query={query}>
        <DataRow label="Slope (centroid)"  value={site.slope_pct != null ? `${site.slope_pct.toFixed(0)}%` : null} source={terrainSource} sourceHref={terrainHref} field="slope_pct" country={country} />
        <DataRow label="Slope (max)"       value={slopeMax != null ? `${Math.round(slopeMax)}%` : null} source="Kartverket DTM 10m" sourceHref={terrainHref} status={slopeMaxStatus} />
        <DataRow label="Slope-suitable area" value={site.buildable_area_ha != null ? `${site.buildable_area_ha.toFixed(1)} ha` : null} source="Kartverket DTM" sourceHref={terrainHref} />
        <DataRow label="Contiguous platform" value={site.buildable_contiguous_ha != null ? `${site.buildable_contiguous_ha.toFixed(1)} ha` : null} source="Kartverket DTM" sourceHref={terrainHref} field="buildable_contiguous_ha" country={country} />
        <DataRow label="Elevation"          value={site.elevation_m != null ? `${Math.round(site.elevation_m)} m a.s.l.` : null} source={terrainSource} sourceHref={terrainHref} field="elevation_m" country={country} />
      </Group>

      <Group title="Network Connectivity" query={query}>
        <DataRow label="Nearest fibre meet" value={site.nearest_ixp_name} source="PeeringDB" mono={false} field="nearest_ixp_name" country={country} />
        <DataRow label="Distance"           value={site.nearest_ixp_distance_km != null ? `${site.nearest_ixp_distance_km.toFixed(1)} km` : null} source="PeeringDB + haversine" field="nearest_ixp_distance_km" country={country} />
        <DataRow label="Facilities ≤50 km"  value={site.facilities_within_50km != null ? `${site.facilities_within_50km}` : null} source="PeeringDB" />
        <DataRow label="Networks at facility" value={site.carriers_estimated != null ? `${site.carriers_estimated}` : null} source="PeeringDB net_count" field="carriers_estimated" country={country} />
      </Group>

      {/* NO-specific network infrastructure — augments PeeringDB above
          with GlobalConnect IXPs/DCs, fiber routes, subsea cables and
          carrier backbones from data_layers.* tables. Per-layer dicts
          come from network_infrastructure_service.py. Renders only when
          at least one layer returned something. */}
      {(site.no_ixp_nearest || site.no_fiber_nearest || site.no_subsea_cable_nearest
          || site.no_cable_landing_nearest || site.no_carrier_backbone_nearest) && (
        <Group title="Network Infrastructure (Norway)" query={query}>
          {site.no_ixp_nearest && (() => {
            const i = site.no_ixp_nearest;
            const flags = [
              i.has_fiber && 'fiber',
              i.has_wavelength && 'wavelength',
              i.has_diverse_fiber && 'diverse fiber',
            ].filter(Boolean).join(' · ');
            const operatorBit = i.operator ? ` · ${i.operator}` : '';
            const cityBit = i.city ? ` (${i.city})` : '';
            const typeBit = i.type ? ` · ${i.type}` : '';
            return (
              <DataRow
                label="Nearest IXP / DC"
                onLocate={locate('ixp', i.name, `${i.name} (IXP / DC)`)}
                value={`${i.name}${cityBit} · ${i.distance_km.toFixed(1)} km${operatorBit}${typeBit}${flags ? ` · ${flags}` : ''}`}
                source="data_layers.ixp_locations"
                mono={false}
              />
            );
          })()}
          {/* Carriers at nearest IXP — full list (sorted by speed desc).
              <details> renders a count summary that expands to the per-ASN
              rows. Wide IXPs (e.g. NIX-OSL) have 30+ networks; the count
              alone is the headline. */}
          {Array.isArray(site.no_ixp_carriers) && site.no_ixp_carriers.length > 0 && (
            <DataRow
              label="Carriers at IXP"
              value={(() => {
                const cs = site.no_ixp_carriers;
                return (
                  <details style={{ display: 'inline' }}>
                    <summary style={{ cursor: 'pointer', display: 'inline' }}>
                      {cs.length} network{cs.length === 1 ? '' : 's'} (click to expand)
                    </summary>
                    <div style={{
                      marginTop: 6,
                      paddingLeft: 12,
                      borderLeft: '2px solid rgba(255,255,255,0.08)',
                      fontSize: 11,
                      color: 'rgba(255,255,255,0.75)',
                    }}>
                      {cs.map((c, idx) => (
                        <div key={idx} style={{ padding: '2px 0' }}>
                          {c.name}
                          {c.asn != null && (
                            <span style={{ color: 'rgba(255,255,255,0.45)' }}> · AS{c.asn}</span>
                          )}
                          {c.speed_mbps != null && (
                            <span style={{ color: 'rgba(255,255,255,0.45)' }}>
                              {' · '}{c.speed_mbps >= 1000 ? `${(c.speed_mbps / 1000).toFixed(0)} Gbps` : `${c.speed_mbps} Mbps`}
                            </span>
                          )}
                          {c.policy && (
                            <span style={{ color: 'rgba(255,255,255,0.45)' }}> · {c.policy}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                );
              })()}
              source="data_layers.ixp_networks (PeeringDB sourced)"
              mono={false}
            />
          )}
          {site.no_fiber_nearest && (() => {
            const f = site.no_fiber_nearest;
            const planned = (f.status || '').toLowerCase() === 'planned';
            return (
              <DataRow
                label="Nearest fiber route"
                onLocate={locate('fiber', f.name, `${f.name} (fibre route)`)}
                value={`${f.name} · ${f.distance_km.toFixed(2)} km${f.operator ? ` · ${f.operator}` : ''}${f.type ? ` · ${f.type}` : ''}${f.status ? ` · ${f.status}${planned ? ' (planned)' : ''}` : ''}`}
                source="data_layers.fiber_networks (GlobalConnect)"
                mono={false}
              />
            );
          })()}
          {site.no_subsea_cable_nearest && (() => {
            const c = site.no_subsea_cable_nearest;
            const planned = (c.status || '').toLowerCase() === 'planned';
            const yr = c.rfs_year ? ` · RFS ${c.rfs_year}` : '';
            const cap = c.capacity_tbps ? ` · ${c.capacity_tbps} Tbps` : '';
            return (
              <DataRow
                label="Nearest subsea cable"
                value={`${c.name} · ${c.distance_km.toFixed(1)} km${c.operator ? ` · ${c.operator}` : ''}${c.status ? ` · ${c.status}${planned && c.rfs_year ? ` (RFS ${c.rfs_year})` : ''}` : ''}${cap}${planned ? '' : yr}`}
                source="data_layers.subsea_cables"
                mono={false}
              />
            );
          })()}
          {site.no_cable_landing_nearest && (() => {
            const l = site.no_cable_landing_nearest;
            const cables = Array.isArray(l.cables) && l.cables.length > 0
              ? ` · cables: ${l.cables.join(', ')}` : '';
            return (
              <DataRow
                label="Nearest cable landing"
                onLocate={locate('cable_landing', l.name, `${l.name} (cable landing)`)}
                value={`${l.name}${l.city ? ` (${l.city})` : ''} · ${l.distance_km.toFixed(1)} km${l.operator ? ` · ${l.operator}` : ''}${cables}`}
                source="data_layers.cable_landing_stations"
                mono={false}
              />
            );
          })()}
          {site.no_carrier_backbone_nearest && (() => {
            const b = site.no_carrier_backbone_nearest;
            const planned = (b.status || '').toLowerCase() === 'planned';
            return (
              <DataRow
                label="Nearest carrier backbone"
                value={`${b.carrier ? `${b.carrier}: ` : ''}${b.name} · ${b.distance_km.toFixed(1)} km${b.type ? ` · ${b.type}` : ''}${b.status ? ` · ${b.status}${planned ? ' (planned)' : ''}` : ''}`}
                source="data_layers.carrier_fiber_networks"
                mono={false}
              />
            );
          })()}
        </Group>
      )}

      <Group title="Land Cover & Zoning" query={query}>
        {(() => {
          const z = resolveZone(site.zone_code, site.zone_name);
          const v = z != null && site.zone_code != null
            ? `${z} (code ${site.zone_code})`
            : z || (site.zone_code != null ? `Code ${site.zone_code}` : null);
          return <DataRow label="Municipal zone" value={v} source={site.zone_layer || 'DIBK NAP'} mono={false} field="zone_code" country={country} />;
        })()}
        {site.zoning_status && (() => {
          // The plan layers read separately (plan_status, 2026-10-17): the
          // status, the plan it rests on with its DIBK record, the conflict
          // action, and any proposal on the land. Same facts as the Projects card.
          const st = site.zoning_status;
          const conflict = !!site.plan_conflict;
          const plan = recordLink('plan', site);
          const pr = site.plan_readings || {};
          const gov = ({ current: pr.reguleringsplan, future_A: pr.kommuneplan,
                         future_B: pr.rp_forslag?.code ? pr.rp_forslag : pr.kp_forslag,
                         not_allowed: pr.reguleringsplan?.code ? pr.reguleringsplan : pr.kommuneplan })[st] || null;
          const when = gov?.in_force ? ` (in force ${gov.in_force})` : gov?.updated ? ` (updated ${gov.updated})` : '';
          const src = gov?.plan_name ? `${gov.layer || 'Plan'}: ${gov.plan_name}${when}` : 'DIBK plan register';
          const status = st === 'not_allowed' ? 'blocker' : (conflict || st === 'future_B') ? 'flagged' : st === 'current' ? 'verified' : undefined;
          const proposals = ['rp_forslag', 'kp_forslag'].map(k => pr[k]).filter(f => f?.code && f.plan_name);
          return (
            <>
              <DataRow label="Zoning status" value={(ZONING_STATUS_LABEL[st] || st) + (conflict ? ' · plan conflict' : '')}
                       source={src} sourceHref={plan?.url || null} status={status} mono={false} field="zoning_status" country={country} />
              <DataRow label="Plan basis" value={site.zoning_status_reason || null} source="DIBK plan register" sourceHref={plan?.url || null} mono={false} />
              {conflict && (
                <DataRow label="Plan conflict" value={site.plan_conflict_text || 'A newer master plan designates something else'} source="pbl § 1-5" status="flagged" mono={false} />
              )}
              {proposals.map(f => (
                <DataRow key={f.plan_id || f.plan_name} label="Plan proposal" value={`${f.plan_name} — ${f.name || f.code}${f.updated ? ` (updated ${f.updated})` : ''}`}
                         source={f.layer || 'DIBK plan proposals'} sourceHref={f.link || null} mono={false} />
              ))}
            </>
          );
        })()}
        {Array.isArray(site.planning_started) && site.planning_started.length > 0 && (
          <DataRow label="Planning started"
                   value={site.planning_started.map(x => `${x.plan_name || x.plan_id}${x.notice_date ? ` (notice ${x.notice_date})` : ''}${x.area_ha != null ? ` · ${Math.round(x.area_ha)} ha` : ''}`).join('; ')}
                   source="DIBK Planlegging igangsatt · boundary only, no land use yet" sourceHref={site.planning_started[0].source_url || null}
                   status="flagged" mono={false} field="planning_started" country={country} />
        )}
        <DataRow label="Land cover (AR5)" value={site.ar5_name ? (site.ar5_name_en && site.ar5_name_en !== site.ar5_name ? `${site.ar5_name} (${site.ar5_name_en})` : site.ar5_name) : null} source="NIBIO AR5" mono={false} field="ar5_code" country={country} />
      </Group>

      <Group title="Environmental & Hazards" query={query}>
        <DataRow label="Flood zone"      value={site.flood_zone ? `Yes (${site.flood_severity || 'severity unknown'})` : 'Not flagged'} source="NVE Flomsonekart" status={site.flood_zone ? 'blocker' : 'verified'} mono={false} field="flood_zone" country={country} />
        <DataRow label="Quick clay"      value={site.kvikkleire_hard_stop ? `Hazard zone — caution${site.kvikkleire_point_count ? ` (${site.kvikkleire_point_count} points)` : ''}` : 'Not flagged'} source="NGU Kvikkleire" status={site.kvikkleire_hard_stop ? 'flagged' : 'verified'} mono={false} field="kvikkleire_hard_stop" country={country} />
        <DataRow label="Salmon river"    value={(site.is_salmon_river || site.has_salmon) ? 'Within 500 m' : 'Not flagged'} source="Miljødirektoratet" status={(site.is_salmon_river || site.has_salmon) ? 'blocker' : 'verified'} mono={false} field="is_salmon_river" country={country} />
        <DataRow label="NiN habitat A"   value={site.nin_type_a ? `Type A: ${site.nin_type_a_name || 'unspecified'}` : 'Not flagged'} source="Miljødirektoratet NiN" status={site.nin_type_a ? 'blocker' : 'verified'} mono={false} field="nin_type_a" country={country} />
        <DataRow label="Cultural monument" value={site.har_kulturminne ? 'Protected within 100 m' : 'Not flagged'} source="Riksantikvaren" status={site.har_kulturminne ? 'blocker' : 'verified'} mono={false} field="har_kulturminne" country={country} />
        <DataRow label="Protected area distance" value={site.nearest_protected_distance_km != null ? `${site.nearest_protected_distance_km.toFixed(1)} km` : null} source="Miljødirektoratet" field="nearest_protected_distance_km"
                 onLocate={locate('protected_area', site.nearest_protected_name || '*', site.nearest_protected_name || 'Nearest protected area')} country={country} />
      </Group>

      <Group title="Water" query={query}>
        <DataRow label="Nearest source" value={site.sea_name || site.river_name || site.lake_name} source={site.sea_name ? "Kartverket coastline" : "NVE Elvenett / Innsjø"} mono={false}
                 onLocate={site.sea_name ? undefined
                           : site.river_name ? locate('river', site.river_name, `${site.river_name} (river)`)
                           : locate('lake', site.lake_name, `${site.lake_name} (lake)`)} field="river_name" country={country} />
        <DataRow label="Distance"       value={(site.water_distance_km ?? site.nearest_water_distance_km) != null ? `${(site.water_distance_km ?? site.nearest_water_distance_km).toFixed(1)} km` : null} source="Spatial proximity" field="water_distance_km" country={country} />
        <DataRow label="Source type"    value={(() => {
          // When the coastline KNN won (sea_name set), the source IS
          // saltwater regardless of what water_source_type says — that
          // field comes from the freshwater pipeline and doesn't know
          // about coastline. Without this override the panel rendered
          // "Nearest source: Sjø" with "Source type: Freshwater",
          // which was internally contradictory.
          if (site.sea_name) return 'Sea (saltwater)';
          const raw = site.water_source_type || site.nearest_water_type;
          if (!raw) return null;
          return WATER_TYPE_LABELS[String(raw).toLowerCase()] || raw;
        })()} source={site.sea_name ? "Kartverket coastline" : "NVE Elvenett / Innsjø"} mono={false} />
      </Group>

      {site.nearest_co2_source && (() => {
        const c = site.nearest_co2_source;
        const bits = [c.distance_km != null ? `${c.distance_km} km away` : null, c.co2_kt ? `${c.co2_kt} kt/yr` : null, c.sector || null].filter(Boolean).join(' · ');
        return (
          <Group title="CO₂ feedstock" query={query}>
            <DataRow label="Nearest CO₂ source" value={`${c.name} · ${bits}`} source="SEIDR CO₂ point-source register" mono={false}
                     onLocate={(typeof onLocate === 'function' && c.lat != null) ? () => onLocate('co2', c.name, `${c.name} (CO₂ feedstock)`, { lat: c.lat, lon: c.lon }) : undefined} />
            {c.operator && <DataRow label="Operator" value={c.operator} source="SEIDR CO₂ point-source register" mono={false} />}
          </Group>
        );
      })()}

      <Group title="Port & export" query={query}>
        <DataRow label="Nearest port" value={site.nearest_port_name || site.port_name} source="Kystverket / Kystdatahuset" mono={false}
                 onLocate={locate('port', site.nearest_port_name || site.port_name, `${site.nearest_port_name || site.port_name} (port)`)} />
        <DataRow label="Distance" value={(site.nearest_port_distance_km ?? site.port_dist_km) != null ? `${Number(site.nearest_port_distance_km ?? site.port_dist_km).toFixed(1)} km` : null} source="Spatial proximity" field="nearest_port_distance_km" />
        <DataRow label="Depth (register)" value={(site.nearest_port_depth_m ?? site.port_depth_m) != null ? `${site.nearest_port_depth_m ?? site.port_depth_m} m${site.nearest_port_depth_estimated ? ' (estimated)' : ''}` : null} source="Kystverket" />
      </Group>

      {/* Climate: NO `free_cooling_days_yr` is derived from a 10-day
          MET Norway forecast extrapolation (the agency has no normals
          API for arbitrary points), not real climate normals; FI uses
          FMI normals. Both are useful for ballpark sizing but not a
          substitute for a site-specific climate study, so we surface
          the verification expectation in the value itself rather than
          letting an over-precise label imply certainty the data can't
          deliver. (Filter form of this signal was cut from the sidebar
          2026-06-03 — see SiteFinder.jsx for context.) */}
      {site.free_cooling_days_yr != null && (
        <Group title="Climate" query={query}>
          <DataRow
            label="Free-cooling days"
            value={`${site.free_cooling_days_yr} days/yr est. — verify with climate study`}
            source="MET Norway forecast (NO) / FMI normals (FI) — proxy data"
          />
        </Group>
      )}
    </div>
    </ProvenanceSite.Provider>
  );
}
